import { Brand } from '@ps/web-core-enums';
import classNames from 'clsx';
import React, { useEffect } from 'react';
import BodyClassName from 'react-body-classname';
import { Helmet } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router';

import { CMSNavigation, CMSReferences, PageBody } from '@swf/cms';
import FloatingWelcomeOffer from '@swf/components/FloatingWelcomeOffer/FloatingWelcomeOffer';
import NPS from '@swf/components/NPS/NPS';
import { CacheControl } from '@swf/components/ResponseHeaders/ResponseHeaders';
import ServiceUnavailable from '@swf/components/ServiceUnavailable/ServiceUnavailable';
import useFirstRender from '@swf/core/hooks/useFirstRender';
import type { Page } from '@swf/core/models/cms';
import { select } from '@swf/core/store';
import { getScrollingElem } from '@swf/core/utils';

import styles from './StarsLayout.scss';
import A2HS from './components/A2HS/A2HS';
import BrowserNotification from './components/BrowserNotification/BrowserNotification';

const scrollingElement = getScrollingElem();

const StarsLayout: React.FC<Page> = (page) => {
  const website = useSelector(select.cms.getWebsite);
  const { menu, depth } = useSelector(select.cms.getMenu);
  const location = useLocation();
  const isSystemAvailable = useSelector(select.system.isAvailable);
  const firstRender = useFirstRender();
  const isEmbedded = useSelector(select.device.isEmbedded);
  const embeddedRoute = useSelector(select.location.isEmbeddedRoute);
  const showWebContent = !(isEmbedded || embeddedRoute);
  const isSportsBrand = useSelector(select.cms.getBrandId) === Brand.BetStars;
  const isPokerBrand = useSelector(select.cms.getBrandId) === Brand.PokerStars;
  const cacheControl = useSelector(select.cms.getCacheControl);

  const {
    uid,
    _content_type_uid,
    locale,
    content,
    template,
    theme,
    layout: {
      background_fit: backgroundFit,
      background_image: backgroundImage,
      background_color: backgroundColor
    }
  } = page;

  const [{ top, side, main }] = content;
  const twoColumns = template === '2 Column';
  const darkTheme = theme === 'Dark';
  const { hide_navigation: hideNavigation } = useSelector(select.cms.getPage) || {};
  const showCMSNav = !hideNavigation && menu && (showWebContent || !isPokerBrand);

  useEffect(() => {
    if (!firstRender && scrollingElement && !location.hash) {
      scrollingElement.scrollTop = 0;
    }
  }, [firstRender, location]);

  return (
    <>
      {cacheControl && <CacheControl>{cacheControl}</CacheControl>}
      <Helmet
        bodyAttributes={{
          'data-pageref': uid,
          'data-contenttype': _content_type_uid,
          'data-locale': locale
        }}
      >
        <html className={styles.html} />
      </Helmet>
      <BodyClassName className={styles.body} />
      {showWebContent && <BrowserNotification />}
      <A2HS />
      {showWebContent && <CMSReferences references={website?.site_header} />}
      {showCMSNav && <CMSNavigation menu={menu} maxDepth={depth || 1} />}
      {isSystemAvailable ? (
        <>
          <CMSReferences references={top} />
          <div
            className={classNames(styles.main, backgroundFit && styles[backgroundFit])}
            style={{
              ...(backgroundImage?.url && {
                backgroundImage: `url(${backgroundImage?.url})`
              }),
              ...(backgroundColor && { backgroundColor })
            }}
          >
            <PageBody side={side} main={main} twoColumns={twoColumns} darkTheme={darkTheme} />
          </div>
        </>
      ) : (
        <>
          <CacheControl>public, max-age=5</CacheControl>
          <ServiceUnavailable />
        </>
      )}
      {(showWebContent || isSportsBrand) && (
        <>
          <CMSReferences references={website?.site_footer} />
          <FloatingWelcomeOffer />
          <NPS />
        </>
      )}
    </>
  );
};

export default StarsLayout;
